import {
  ApplicationConfig,
  provideZoneChangeDetection,
  inject,
  APP_INITIALIZER,
} from '@angular/core';
import {
  InMemoryScrollingFeature,
  InMemoryScrollingOptions,
  provideRouter,
  withInMemoryScrolling,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withFetch } from '@angular/common/http';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { environment } from '@environments/environment';
import { routes } from '@app/app.routes';
import { AuthenticationService } from './core/services/authentication/authentication.service';
import { BrowserCheckService } from './utils/browser-check.service';

const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
};

const inMemoryScrollingFeature: InMemoryScrollingFeature =
  withInMemoryScrolling(scrollConfig);

function authInitializerFactory() {
  const authService = inject(AuthenticationService);
  const browserCheck = inject(BrowserCheckService);

  return () => {
    if (browserCheck.isBrowser()) {
      authService.initializeAuthentication();
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withViewTransitions(),
      withRouterConfig({}),
      inMemoryScrollingFeature,
    ),
    provideClientHydration(),
    provideAnimationsAsync(),
    provideHttpClient(withFetch()),

    provideApollo(() => {
      const httpLink = inject(HttpLink);

      // Handle authentication via Authorization header
      const authLink = new ApolloLink((operation, forward) => {
        const token = environment.previewMode
          ? environment.contentful.previewAccessToken
          : environment.contentful.accessToken;

        operation.setContext({
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        return forward(operation);
      });

      // Error handling for GraphQL and network errors
      const errorLink = onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors) {
          console.error('[GraphQL Errors]', graphQLErrors);
        }
        if (networkError) {
          console.error('[Network Error]', networkError);
        }
      });

      // Combine error and auth links with HTTP link
      return {
        link: ApolloLink.from([
          errorLink,
          authLink,
          httpLink.create({ uri: environment.contentful.graphqlEndpoint }),
        ]),
        cache: new InMemoryCache(),
        defaultOptions: {
          watchQuery: {
            fetchPolicy: 'cache-and-network',
            errorPolicy: 'all',
          },
          query: {
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
          },
        },
      };
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: authInitializerFactory,
      multi: true,
    },
  ],
};
